/**
 * The translation catalogs beyond the startup keys, loaded namespace by
 * namespace.
 *
 * The startup chunk carries only the keys the login form, the shell and
 * Home's first frame use (startupKeys.json). Each top-level key of the
 * catalog (`lists`, `calendar`, ...) is a namespace in a chunk of its own,
 * merged into the one `translation` resource, so `t('lists.…')` reads the same
 * whichever way its text arrived:
 *
 * - a view's loader waits for its namespaces with its code (`withCatalogs`),
 *   so a view never renders a raw key; a page outside the shell loads its own
 *   (`loadCatalogs` in its route). `pnpm audit:i18n` checks both lists
 *   against the keys the code uses.
 * - once the first screen settles, the rest of the current language loads in
 *   the background, one namespace per idle moment (`loadRemainingCatalogs`).
 * - a key whose namespace is still missing loads it and renders again, for
 *   keys no list names (`loadNamespaceOfMissingKey`).
 * - a language switch loads the new language's startup keys and every
 *   namespace asked for so far before it switches (`prepareLanguage`).
 */
import type { i18n as I18n } from 'i18next';

import type en from './locales/en';
import {
  DEFAULT_LANGUAGE,
  getInitialLanguage,
  loadStartupCatalog,
  normalizeLanguage,
  type SupportedLanguage,
} from './languages';

export type CatalogNamespace = keyof typeof en;

/** Emitted on i18n after a namespace arrived for a key that rendered raw. */
export const CATALOGS_ADDED_EVENT = 'rudiCatalogsAdded';

const namespaceLoaders = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  import: 'default',
});

const NAMESPACES: readonly string[] = Object.keys(namespaceLoaders)
  .filter(path => path.startsWith(`./locales/${DEFAULT_LANGUAGE}/`))
  .map(path => path.slice(`./locales/${DEFAULT_LANGUAGE}/`.length, -'.json'.length));

const loaded = new Map<SupportedLanguage, Set<string>>();
const pending = new Map<string, Promise<void>>();
/** Every namespace a view or page asked for, which a language switch brings along. */
const requested = new Set<string>();
const startupLoaded = new Set<SupportedLanguage>([DEFAULT_LANGUAGE]);

// Kept free of a static i18next import: the view loaders the startup script
// starts (initialViewPreload.ts) fetch their namespaces before the i18n setup
// arrives, and index.ts hands over the instance with `connectCatalogs`.
let i18n: I18n | null = null;
const arrived: Array<[SupportedLanguage, string, unknown]> = [];

function addNamespace(language: SupportedLanguage, namespace: string, data: unknown): void {
  if (i18n) i18n.addResourceBundle(language, 'translation', { [namespace]: data }, true, true);
  else arrived.push([language, namespace, data]);
}

/** Called by index.ts once i18next is initialized; adds what arrived before. */
export function connectCatalogs(instance: I18n): void {
  i18n = instance;
  for (const [language, namespace, data] of arrived.splice(0)) {
    addNamespace(language, namespace, data);
  }
}

function connected(): I18n {
  if (!i18n) throw new Error('i18n is not initialized');
  return i18n;
}

function loadedIn(language: SupportedLanguage): Set<string> {
  let namespaces = loaded.get(language);
  if (!namespaces) {
    namespaces = new Set();
    loaded.set(language, namespaces);
  }
  return namespaces;
}

function currentLanguage(): SupportedLanguage {
  return i18n ? (normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE) : getInitialLanguage();
}

export function isCatalogNamespace(name: string): name is CatalogNamespace {
  return NAMESPACES.includes(name);
}

/** Record that a language's startup catalog holds every namespace. */
export function markCatalogComplete(language: SupportedLanguage): void {
  startupLoaded.add(language);
  for (const namespace of NAMESPACES) loadedIn(language).add(namespace);
}

function loadNamespace(language: SupportedLanguage, namespace: string): Promise<void> {
  if (loadedIn(language).has(namespace)) return Promise.resolve();
  const name = `${language}/${namespace}`;
  let load = pending.get(name);
  if (load) return load;
  const loader = namespaceLoaders[`./locales/${name}.json`];
  if (!loader) return Promise.resolve();
  load = loader().then(
    data => {
      addNamespace(language, namespace, data);
      loadedIn(language).add(namespace);
      pending.delete(name);
    },
    (error: unknown) => {
      // A failed import may succeed on a later attempt.
      pending.delete(name);
      throw error;
    }
  );
  pending.set(name, load);
  return load;
}

function loadNamespacesIn(
  language: SupportedLanguage,
  namespaces: Iterable<string>
): Promise<void> {
  return Promise.all([...namespaces].map(namespace => loadNamespace(language, namespace))).then(
    () => undefined
  );
}

/** Load namespaces of the current language; resolves once `t` reads them. */
export function loadCatalogs(namespaces: readonly CatalogNamespace[]): Promise<void> {
  for (const namespace of namespaces) requested.add(namespace);
  return loadNamespacesIn(currentLanguage(), namespaces);
}

/**
 * A loader that resolves with the module once its code and its namespaces are
 * in, for the cached view loaders (platform/performance/viewModules.ts).
 */
export function withCatalogs<Module>(
  namespaces: readonly CatalogNamespace[],
  importer: () => Promise<Module>
): () => Promise<Module> {
  return () => Promise.all([importer(), loadCatalogs(namespaces)]).then(([module]) => module);
}

/**
 * Load a language's startup keys and the namespaces asked for so far, so
 * switching to it never shows a missing key.
 */
export async function prepareLanguage(language: SupportedLanguage): Promise<void> {
  // English is bundled with the startup code.
  if (!startupLoaded.has(language) && language !== 'en') {
    const startup = await loadStartupCatalog(language);
    // A namespace that arrived first keeps its complete text.
    connected().addResourceBundle(language, 'translation', startup.default, true, false);
    if (startup.complete) markCatalogComplete(language);
    startupLoaded.add(language);
  }
  await loadNamespacesIn(language, requested);
}

/** i18next's missingKeyHandler: load the key's namespace and render again. */
export function loadNamespaceOfMissingKey(key: string): void {
  const namespace = key.split('.', 1)[0] ?? '';
  const language = currentLanguage();
  if (!isCatalogNamespace(namespace) || loadedIn(language).has(namespace)) return;
  loadCatalogs([namespace]).then(
    () => connected().emit(CATALOGS_ADDED_EVENT),
    (error: unknown) => console.warn(`[i18n] Loading ${namespace} failed:`, error)
  );
}

let remainingStarted = false;
let remainingRunning = false;
let userIsWaiting: () => boolean = () => false;
const remainingFailed = new Set<string>();

function idle(callback: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 2_000 });
  } else {
    setTimeout(callback, 50);
  }
}

function loadNextRemaining(): void {
  if (remainingRunning) return;
  const language = currentLanguage();
  const namespace = NAMESPACES.find(
    name => !loadedIn(language).has(name) && !remainingFailed.has(`${language}/${name}`)
  );
  if (namespace === undefined) return;
  remainingRunning = true;
  idle(() => {
    // Never compete with a screen the user is waiting for.
    const load = userIsWaiting() ? Promise.resolve() : loadNamespace(language, namespace);
    load
      .catch((error: unknown) => {
        remainingFailed.add(`${language}/${namespace}`);
        console.warn(`[i18n] Loading ${namespace} failed:`, error);
      })
      .finally(() => {
        remainingRunning = false;
        loadNextRemaining();
      });
  });
}

/**
 * Load every namespace of the current language that is not in yet, one per
 * idle moment, and again after a language switch. The safety net for a key
 * that no view's list names; the shell starts it once the first screen
 * settles.
 */
export function loadRemainingCatalogs(options: { userIsWaiting?: () => boolean } = {}): void {
  if (remainingStarted) return;
  remainingStarted = true;
  if (options.userIsWaiting) userIsWaiting = options.userIsWaiting;
  connected().on('languageChanged', loadNextRemaining);
  loadNextRemaining();
}
